import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { EmptyState } from '../../../components/ui/empty-state'
import { useUser } from '../../../hooks/useUser'
import { scheduledSegmentsApi, scheduledSegmentsApiError } from '../api'
import {
    formatScheduleOccurrence,
    formatScheduleSkipReason,
    SCHEDULE_SKIP_REASON_COPY,
} from '../format'
import type { BigintId, ScheduleRun, ScheduleRunHistory } from '../types'

const PAGE_SIZE = 25

export interface ScheduledRunsTableProps {
    leadingActions?: React.ReactNode
    /** Primarily useful to embed the table outside Combined History and in tests. */
    profileId?: BigintId | null
    /** Restrict history to one saved segment when embedded on its detail page. */
    recipeId?: BigintId | null
    /** Override the heading when the table is embedded in another page. */
    title?: string
}

const formatResult = (result: unknown): string => {
    if (result == null) return '—'
    if (typeof result === 'string') return result
    if (typeof result === 'number' || typeof result === 'boolean') return String(result)

    try {
        return JSON.stringify(result)
    } catch {
        return String(result)
    }
}

const formatReason = (reason: string | null): string => {
    if (!reason) return '—'
    const copy = formatScheduleSkipReason(reason)
    if (copy && reason in SCHEDULE_SKIP_REASON_COPY) return copy
    return reason
        .replace(/^skipped_/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

const formatFreshness = (value: string | null, timezone: string): string => {
    if (!value) return '—'
    return formatScheduleOccurrence(value, timezone)
}

const statusClassName = (status: ScheduleRun['status']): string => {
    if (status === 'completed') return 'text-[#238551]'
    if (status === 'failed') return 'text-[#cd4246]'
    if (status === 'skipped') return 'text-[#935610]'
    return 'text-[#5f6b7c]'
}

/** The profile-scoped execution history for scheduled segments. */
export const ScheduledRunsTable = ({
    leadingActions = null,
    profileId: requestedProfileId,
    recipeId = null,
    title = 'Scheduled segments',
}: ScheduledRunsTableProps) => {
    const { profile, activeProfile } = useUser()
    const profileTimezone = activeProfile?.timezone ?? 'UTC'
    const profileId =
        requestedProfileId === undefined
            ? profile == null
                ? null
                : String(profile)
            : requestedProfileId
    const [items, setItems] = useState<ScheduleRun[]>([])
    const [nextCursor, setNextCursor] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const requestGeneration = useRef(0)

    const load = useCallback(
        async (
            cursor: string | null,
            append: boolean,
            generation: number,
            activeProfileId: BigintId,
        ) => {
            setLoading(true)
            setError(null)
            try {
                const history: ScheduleRunHistory = await scheduledSegmentsApi.history(
                    activeProfileId,
                    cursor
                        ? { limit: PAGE_SIZE, cursor, ...(recipeId ? { recipeId } : {}) }
                        : { limit: PAGE_SIZE, ...(recipeId ? { recipeId } : {}) },
                )
                if (requestGeneration.current !== generation) return
                setItems((current) => (append ? [...current, ...history.items] : history.items))
                setNextCursor(history.next_cursor)
            } catch (requestError) {
                if (requestGeneration.current !== generation) return
                setError(scheduledSegmentsApiError(requestError))
            } finally {
                if (requestGeneration.current === generation) setLoading(false)
            }
        },
        [recipeId],
    )

    useEffect(() => {
        const generation = requestGeneration.current + 1
        requestGeneration.current = generation
        setItems([])
        setNextCursor(null)
        setError(null)
        if (!profileId) {
            setLoading(false)
            return () => {
                requestGeneration.current += 1
            }
        }

        void load(null, false, generation, profileId)

        return () => {
            requestGeneration.current += 1
        }
    }, [load, profileId, recipeId])

    const retry = () => {
        if (!profileId) return
        const generation = requestGeneration.current + 1
        requestGeneration.current = generation
        void load(null, false, generation, profileId)
    }

    const loadMore = () => {
        if (!profileId || !nextCursor || loading) return
        const generation = requestGeneration.current
        void load(nextCursor, true, generation, profileId)
    }

    return (
        <Card className="flex flex-col overflow-hidden p-0">
            <div className="flex min-h-[52px] items-center justify-between gap-4 border-b border-[#E5E7EB] px-[20px] py-[10px]">
                <h2 className="m-0 text-[16px] font-semibold text-[#182026]">{title}</h2>
                {leadingActions}
            </div>

            {loading && items.length === 0 ? (
                <div className="min-h-[180px] p-[20px]" role="status">
                    Loading scheduled runs…
                </div>
            ) : error && items.length === 0 ? (
                <EmptyState
                    className="min-h-[180px] p-[20px]"
                    title="Could not load scheduled runs"
                    description={error}
                    action={
                        <Button type="button" onClick={retry} text="Try again" intent="primary" />
                    }
                />
            ) : items.length === 0 ? (
                <EmptyState
                    className="min-h-[180px] p-[20px]"
                    title="No scheduled runs"
                    description="Scheduled segment runs will appear here once they execute."
                />
            ) : (
                <>
                    {error && (
                        <div
                            className="border-b border-[#F0B8B8] bg-[#FFF5F5] px-[20px] py-[10px] text-sm text-[#A82A2A]"
                            role="alert"
                        >
                            {error}{' '}
                            <button type="button" className="underline" onClick={retry}>
                                Try again
                            </button>
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[960px] border-collapse text-left text-[13px]">
                            <thead className="bg-[#F7F8FA] text-[12px] font-semibold uppercase tracking-wide text-[#5F6B7C]">
                                <tr>
                                    <th className="px-[20px] py-[12px]">Scheduled for</th>
                                    <th className="px-[12px] py-[12px]">Segment</th>
                                    <th className="px-[12px] py-[12px]">Status</th>
                                    <th className="px-[12px] py-[12px]">Reason</th>
                                    <th className="px-[12px] py-[12px]">Result</th>
                                    <th className="px-[12px] py-[12px]">Freshness</th>
                                    <th className="px-[20px] py-[12px]">Audit</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E5E7EB] text-[#1C2127]">
                                {items.map((run) => (
                                    <tr key={run.id}>
                                        <td className="whitespace-nowrap px-[20px] py-[14px]">
                                            {formatScheduleOccurrence(
                                                run.scheduled_for_at,
                                                run.schedule_snapshot?.timezone ?? 'UTC',
                                            )}
                                        </td>
                                        <td className="max-w-[220px] px-[12px] py-[14px]">
                                            <Link
                                                className="text-[#2D72D2] hover:underline"
                                                to={`/segments/${run.recipe_id}`}
                                            >
                                                {run.recipe_name}
                                            </Link>
                                        </td>
                                        <td
                                            className={`whitespace-nowrap px-[12px] py-[14px] font-medium ${statusClassName(run.status)}`}
                                        >
                                            {run.status}
                                        </td>
                                        <td
                                            className="max-w-[260px] px-[12px] py-[14px]"
                                            title={run.reason_code ?? undefined}
                                        >
                                            {formatReason(run.reason_code)}
                                        </td>
                                        <td className="max-w-[260px] whitespace-pre-wrap break-words px-[12px] py-[14px]">
                                            {formatResult(run.result)}
                                        </td>
                                        <td className="whitespace-nowrap px-[12px] py-[14px]">
                                            {formatFreshness(
                                                run.freshness_observed_at,
                                                profileTimezone,
                                            )}
                                        </td>
                                        <td className="px-[20px] py-[14px]">
                                            {run.audit_log_id ? (
                                                <Link
                                                    className="text-[#2D72D2] hover:underline"
                                                    to={`/audit-logs/${run.audit_log_id}`}
                                                >
                                                    View audit log
                                                </Link>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {nextCursor && (
                        <div className="flex justify-center border-t border-[#E5E7EB] px-[20px] py-[14px]">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={loadMore}
                                loading={loading}
                                disabled={loading}
                                text="Load more"
                            />
                        </div>
                    )}
                </>
            )}
        </Card>
    )
}
